import React, { useState, useCallback, useMemo } from 'react';
import type { Value } from 'platejs';
import styles from './MagicSign.module.scss';
import useMagicSignLogic from './useMagicSignLogic';
import MagicSignTable from './components/MagicSignTable/MagicSignTable';
import AuditLogDrawer from './components/AuditLogDrawer/AuditLogDrawer';
import CancelConfirmDialog from './components/CancelConfirmDialog/CancelConfirmDialog';
import ComposeEmailModal from '../ComposeEmailModal/ComposeEmailModal';
import MagicSignPreviewer from './MagicSignPreviewer';
import MagicUploadCenter from '../MagicUpload/components/MagicUploadCenter/MagicUploadCenter';
import { MagicSignRow, MagicSignStatus, SignStatus } from './types';
import { useSearchParams } from 'react-router-dom';
import { sendMagicSignEmail } from './MagicSignService';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../app/hooks';
import { MAGIC_SIGN_ALLOWED_TYPES, MAGIC_SIGN_PDF_ONLY_TOAST } from './constants';

/**
 * Builds the initial email body as a structured Plate Value with per-signatory
 * signing links. Returning Value (not HTML) ensures the Plate editor renders real
 * formatting (bold, clickable links) and that plateToHtml round-trips to proper
 * <strong>/<a> tags — not escaped HTML-as-text.
 *
 * Shape produced:
 *   [{ type:'p', children:[{ text:'Hi,' }] },
 *    { type:'p', children:[{ text:'' }] },                   ← blank line
 *    { type:'p', children:[{ text:'DocLabel', bold:true }, { text:' has been shared by Actor.' }] },
 *    { type:'p', children:[{ text:'' }] },                   ← blank line
 *    { type:'p', children:[{ text:'Please review and sign.' }] },
 *    ... one paragraph per signatory with link element ...
 *   ]
 */
export const buildMagicSignNoteWithLinks = ({
    documentType,
    actorName,
    signingLinks,
}: {
    documentType?: string;
    actorName?: string;
    signingLinks: Array<{ signatoryId: string; link: string; name: string; orgName?: string }>;
}): Value => {
    const docLabel = documentType ? documentType : 'The document';
    const sharedBy = actorName ? ` by ${actorName}.` : '.';

    // Paragraph 1: "Hi,"
    const hiPara = { type: 'p', children: [{ text: 'Hi,' }] };
    // Blank paragraph to simulate the double-line-break in the original HTML.
    const blank = { type: 'p', children: [{ text: '' }] };
    // Paragraph 2: bold document label + rest of sentence.
    const bodyPara = {
        type: 'p',
        children: [{ text: docLabel, bold: true }, { text: ` has been shared${sharedBy}` }],
    };
    // Paragraph 3: "Please review and sign."
    const signPara = { type: 'p', children: [{ text: 'Please review and sign.' }] };

    const nodes: any[] = [hiPara, blank, bodyPara, blank, signPara];

    // One paragraph per signatory: "Name (Org): <link>"
    if (signingLinks.length > 0) {
        nodes.push(blank);
        for (const sl of signingLinks) {
            const label = sl.orgName ? `${sl.name} (${sl.orgName}): ` : `${sl.name}: `;
            nodes.push({
                type: 'p',
                children: [{ text: label }, { type: 'a', url: sl.link, children: [{ text: sl.link }] }],
            });
        }
    }

    return nodes as Value;
};

interface MagicSignProps {
    /** Phase 3 hook: externally injected row to highlight and show first */
    highlightedRowId?: string | null;
    /**
     * Gates the initial list fetch. Set to true when the Magic Sign tab is first
     * shown so the fetch is deferred until the user activates the tab.
     * Once fetched, the state is preserved across tab switches (no re-fetch on revisit).
     */
    isActive?: boolean;
}

const MagicSign: React.FC<MagicSignProps> = ({ highlightedRowId: externalHighlightedRowId, isActive = true }) => {
    const [searchParams] = useSearchParams();
    const companyName = searchParams.get('companyName') ?? undefined;
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const actorName: string = platformData?.displayName ?? '';

    // Email composer state
    const [emailModalRow, setEmailModalRow] = useState<MagicSignRow | null>(null);
    // 'reminder' → opened from Send Reminder; on send we fire the reminder (pending signatories only).
    const [emailModalMode, setEmailModalMode] = useState<'email' | 'reminder'>('email');

    const logic = useMagicSignLogic({ isActive });

    const {
        rows,
        loading,
        configMeta,
        polusDocTypes,
        activeRowId,
        auditLogOpen,
        setAuditLogOpen,
        auditLogEvents,
        auditLogLoading,
        auditLogRowName,
        cancelConfirmId,
        setCancelConfirmId,
        docIdToView,
        setDocIdToView,
        docNameToView,
        activeFilters,
        handleFiltersChange,
        handleFileUpload,
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleGenerateLinks,
        handleSendReminder,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
        refreshList,
    } = logic;

    const effectiveHighlightedRowId = externalHighlightedRowId ?? null;

    // PDF-only filter: toast on non-PDF and skip, then delegate to useMagicSignLogic handler.
    // handleFileUpload takes File[] directly — no FileList-shaped object needs to be faked.
    const handleFilesSelected = useCallback(
        (files: FileList) => {
            const filtered: File[] = [];
            for (let i = 0; i < files.length; i++) {
                if (!MAGIC_SIGN_ALLOWED_TYPES.includes(files[i].type)) {
                    toast.error(MAGIC_SIGN_PDF_ONLY_TOAST);
                } else {
                    filtered.push(files[i]);
                }
            }
            if (filtered.length === 0) return;
            handleFileUpload(filtered);
        },
        [handleFileUpload],
    );

    // Generate links action — called from 3-dot menu "Generate Links"
    const handleGenerateLinksAction = useCallback(
        async (row: MagicSignRow) => {
            if (!row.signatories?.length) {
                toast.error('Select at least one signatory before generating links.');
                return;
            }
            await handleGenerateLinks(row.id);
        },
        [handleGenerateLinks],
    );

    // Send Email / Send Reminder — routes based on new status model + emailSentAt.
    // GENERATED_LINKS && !emailSentAt → email mode (first send)
    // GENERATED_LINKS && emailSentAt  → reminder mode
    // PARTIAL_SIGNED                  → reminder mode
    const handleStatusAction = useCallback(async (row: MagicSignRow) => {
        const isReminder =
            (row.status === MagicSignStatus.GENERATED_LINKS && !!row.emailSentAt) || row.status === MagicSignStatus.PARTIAL_SIGNED;
        if (isReminder) {
            setEmailModalMode('reminder');
            setEmailModalRow(row);
            return;
        }
        if (row.status === MagicSignStatus.GENERATED_LINKS && !row.emailSentAt) {
            if (!row.signatories?.length) {
                toast.error('Select at least one signatory before sending.');
                return;
            }
            setEmailModalMode('email');
            setEmailModalRow(row);
        }
    }, []);

    const handleEmailSent = useCallback(() => {
        refreshList();
    }, [refreshList]);

    // Build the onSendOverride for ComposeEmailModal — calls sendMagicSignEmail then closes modal
    const handleMagicSignSend = useCallback(
        async (recipients: Array<{ emailId: string; userId?: string }>, noteHtml: string) => {
            if (!emailModalRow) return;
            if (emailModalMode === 'reminder') {
                await handleSendReminder(emailModalRow.id);
                setEmailModalRow(null);
                return;
            }
            const recipientEmails = recipients.map((r) => r.emailId).filter(Boolean);
            const note = noteHtml && noteHtml !== '<div></div>' ? noteHtml : undefined;
            try {
                await sendMagicSignEmail({ id: emailModalRow.id, recipientEmails, note, logoUrl: platformData?.logoUrl });
                toast.success('Signing email sent successfully.');
                handleEmailSent();
                setEmailModalRow(null);
            } catch (error) {
                console.error('[MagicSign] Failed to send signing email', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to send signing email. Please try again.');
            }
        },
        [emailModalRow, emailModalMode, handleSendReminder, handleEmailSent, platformData],
    );

    // Memoized so ComposeEmailModal's fetchUsers effect (keyed on initialRecipients) only
    // refetches org users when the modal actually opens for a different row/mode — a fresh
    // .filter().map() literal here would change identity every render and refetch on every
    // parent re-render while the modal stays open.
    const emailModalRecipients = useMemo(
        () =>
            (emailModalRow?.signatories ?? [])
                .filter((s) => (emailModalMode === 'reminder' ? s.signStatus !== SignStatus.SIGNED : true))
                .map((s) => ({
                    emailId: s.email,
                    userId: s.userId,
                    name: s.name,
                })),
        [emailModalRow, emailModalMode],
    );

    return (
        <div className={styles.MagicSignContainer}>
            {/* Upload zone — reuses MagicUploadCenter exactly as Magic Upload does,
                with onFilesSelected wired to our PDF-only filter + MagicSign upload chain,
                and acceptTypes restricting the file picker to PDF only. */}
            <MagicUploadCenter
                tabState="all"
                fileData={[]}
                setFileData={() => {}}
                orientation="vertical"
                onFilesSelected={handleFilesSelected}
                acceptTypes="application/pdf"
                dropText="Drag and drop any data"
            />

            {/* Table */}
            <MagicSignTable
                rows={rows}
                loading={loading}
                configMeta={configMeta}
                polusDocTypes={polusDocTypes}
                activeRowId={activeRowId}
                highlightedRowId={effectiveHighlightedRowId}
                activeFilters={activeFilters}
                onFiltersChange={handleFiltersChange}
                onRowClick={handleRowClick}
                onUpdateDocType={handleUpdateDocType}
                onUpdateSignType={handleUpdateSignType}
                onSaveSignatories={handleUpdateSignatories}
                onGenerateLinks={handleGenerateLinksAction}
                onSendEmail={handleStatusAction}
                onViewAuditLog={handleViewAuditLog}
                onDownloadDocument={handleDownloadDocument}
                onCopySigningLinks={handleCopySigningLinks}
                onCancelRequest={(id) => setCancelConfirmId(id)}
            />

            {/* Audit log drawer */}
            <AuditLogDrawer
                open={auditLogOpen}
                onClose={() => setAuditLogOpen(false)}
                documentName={auditLogRowName}
                companyName={companyName}
                events={auditLogEvents}
                loading={auditLogLoading}
            />

            {/* PDF previewer (reuses MagicUpload's previewer) */}
            <MagicSignPreviewer docIdToView={docIdToView} setDocIdToView={setDocIdToView} docName={docNameToView} />

            {/* Email composer — signing links are appended into the email body (Task D) */}
            {emailModalRow && (
                <ComposeEmailModal
                    isOpen={!!emailModalRow}
                    headerTitle={emailModalMode === 'reminder' ? 'Reminder' : 'Email'}
                    headerSubTitle={emailModalRow.fileName}
                    initialRecipients={emailModalRecipients}
                    initialNote={buildMagicSignNoteWithLinks({
                        documentType: emailModalRow.documentType,
                        actorName,
                        signingLinks: emailModalRow.signingLinks ?? [],
                    })}
                    onSendOverride={handleMagicSignSend}
                    onCloseOverride={() => setEmailModalRow(null)}
                />
            )}

            {/* Cancel confirmation */}
            {cancelConfirmId && (
                <CancelConfirmDialog onConfirm={() => handleCancelConfirm(cancelConfirmId)} onDismiss={() => setCancelConfirmId(null)} />
            )}
        </div>
    );
};

export default MagicSign;
